// LE REFUS 422 STRUCTURÉ DU RAPPROCHEMENT COMPTABLE (E-07, lot 6 tâche 12, consommé ici en
// tâche 14). `service.handler.ts` (engine, microservice `order`) jette
// `UnprocessableEntityException({ message, code, manquants | ecarts | nonAlignes })` — porté
// tel quel jusqu'au dashboard par le `GlobalExceptionFilter` (`{ data, message, statusCode }`,
// où `data` EST ce corps structuré : voir aussi `CatalogPublishModal.tsx`,
// `error?.data?.data?.message`, même convention). Extrait en logique PURE (comme
// `fiscal.logic.ts`) pour être testé sans monter tout `ServiceList`.
export const CODES_REFUS_EXPORT_COMPTABLE = ['PERIODE_NON_CLOTUREE', 'DIVERGENCE_GRAND_TOTAL', 'FENETRE_NON_ALIGNEE'] as const;
export type CodeRefusExportComptable = typeof CODES_REFUS_EXPORT_COMPTABLE[number];

export interface IManquantExportComptable { registerCode: string; piece: string }
export interface INonAligneExportComptable { registerCode: string; cloture: string; piece: string }
export interface IEcartExportComptable { taux: number; service: number; registre: number; ecart: number }

export interface DetailRefusExportComptable {
  code: string;
  message?: string;
  manquants?: IManquantExportComptable[];
  ecarts?: IEcartExportComptable[];
  nonAlignes?: INonAligneExportComptable[];
}

// `AUCUN_REGISTRE` N'EST JAMAIS UN REFUS (T12) : le fichier part quand même (non rapproché,
// non scellé — cf. `FiscalAccountingExports.tsx`). Seuls les trois codes ci-dessus arrêtent
// l'export avant même de produire un fichier.
export const estRefusExportComptableStructure = (detail: unknown): detail is DetailRefusExportComptable =>
  typeof detail === 'object'
  && detail !== null
  && typeof (detail as { code?: unknown }).code === 'string'
  && (CODES_REFUS_EXPORT_COMPTABLE as readonly string[]).includes((detail as { code: string }).code);

// Montants du rapprochement en CENTIMES (`fiscal-accounting-control.service.ts`,
// `TOLERANCE_CENTIMES`, `ClosureRateLine.ttc: cents`) : jamais affichés bruts, toujours
// convertis en euros lisibles.
const euros = (centimes: number): string => `${(centimes / 100).toFixed(2).replace('.', ',')} €`;

// Une ligne par caisse/écart concerné — registre, clôture, champ en cause, attendu/obtenu en
// euros quand c'est pertinent (E-07, brief tâche 14).
export const detailRefusExportComptable = (detail: DetailRefusExportComptable): string[] => {
  if (detail.code === 'PERIODE_NON_CLOTUREE') {
    return (detail.manquants ?? []).map((m) => `Caisse ${m.registerCode} : la pièce ${m.piece} n'est couverte par aucune clôture fiscale.`);
  }

  if (detail.code === 'FENETRE_NON_ALIGNEE') {
    return (detail.nonAlignes ?? []).map((n) => `Caisse ${n.registerCode} : la clôture ${n.cloture} couvre aussi la pièce ${n.piece}, hors de la période exportée.`);
  }

  if (detail.code === 'DIVERGENCE_GRAND_TOTAL') {
    return (detail.ecarts ?? []).map((e) => `TVA ${e.taux}% : service ${euros(e.service)}, registre ${euros(e.registre)} — écart ${euros(e.ecart)}.`);
  }

  return [];
};

// Rappel court, sans jargon, affiché au-dessus du détail par caisse.
export const titreRefusExportComptable = (detail: DetailRefusExportComptable): string => {
  if (detail.code === 'PERIODE_NON_CLOTUREE') return "Export refusé : la période n'est pas encore clôturée.";
  if (detail.code === 'FENETRE_NON_ALIGNEE') return 'Export refusé : la fenêtre exportée ne coïncide pas avec un Z de caisse.';
  if (detail.code === 'DIVERGENCE_GRAND_TOTAL') return 'Export refusé : divergence avec le Grand Total signé.';
  return detail.message ?? 'Export refusé.';
};

// LE SCEAU RETOURNÉ PAR L'EXPORT RÉUSSI (tâche 13) : `scelle: true` pour un export rapproché,
// `scelle: false` (avec `raison: 'AUCUN_REGISTRE'`) pour une boutique sans registre — jamais
// un refus (T12 refuse AVANT de produire un fichier). Jamais « authentifié » : seul le crédit
// (TTC par taux) est rapproché (I5, tâche 12).
export const messageExportScelle = (sceau: { scelle?: boolean; sha256?: string | null; raison?: string | null } | undefined | null): string | null => {
  if (!sceau) return null;

  if (sceau.scelle) {
    return `Export téléchargé et scellé — empreinte ${String(sceau.sha256 ?? '').slice(0, 16)}…`;
  }

  return `Export téléchargé, non scellé (${sceau.raison ?? 'boutique sans registre fiscal'}).`;
};
